
import { supabase } from '../client';
import { User, GameDurationType } from '@/types';
import { isValidUUID } from '../utils/validationUtils';

interface CreateGameOptions {
  name: string;
  courseId: string;
  players: User[];
  bet?: string;
  durationType?: GameDurationType;
  isLoneWolf?: boolean;
  isRecurring?: boolean;
  recurringGroupId?: string | null;
  startDate?: string;
  endDate?: string | null;
}

export const createGame = async (
  nameOrOptions: string | CreateGameOptions,
  courseId?: string,
  players?: User[],
  bet?: string
) => {
  // Support both old positional args and new options object
  const opts: CreateGameOptions = typeof nameOrOptions === 'string'
    ? { name: nameOrOptions, courseId: courseId!, players: players!, bet }
    : nameOrOptions;
  try {
    console.log("Creating game with params:", { name: opts.name, courseId: opts.courseId, playersCount: opts.players.length, bet: opts.bet });

    // Validate UUID format
    if (!opts.courseId || !isValidUUID(opts.courseId)) {
      console.error('Invalid courseId format:', opts.courseId);
      return { error: new Error('Invalid course ID format') };
    }
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('No authenticated user found');
      return { error: new Error('User must be authenticated to create a game') };
    }

    console.log("Current authenticated user:", user.id);
    
    // Validate course exists
    const { data: course, error: courseError } = await supabase
      .from('courses')
      .select('id')
      .eq('id', opts.courseId)
      .maybeSingle();
      
    if (courseError || !course) {
      console.error('Course not found:', courseError);
      return { error: courseError || new Error('Course not found') };
    }
    
    // Create game data - include user_id to satisfy RLS policy
    const gameData = {
      name: opts.name,
      course_id: opts.courseId,
      start_date: opts.startDate ?? new Date().toISOString(),
      end_date: opts.endDate ?? null,
      game_type: "Birdie Game",
      duration_type: opts.durationType ?? 'open_ended' as const,
      is_lone_wolf: opts.isLoneWolf ?? false,
      is_recurring: opts.isRecurring ?? false,
      recurring_group_id: opts.recurringGroupId ?? null,
      bet: opts.bet,
      user_id: user.id  // Required by RLS policy
    };
    
    console.log("Creating game with data:", gameData);
    
    // Insert the game - the RLS policy will check user_id = auth.uid()
    const { data: game, error: gameError } = await supabase
      .from('games')
      .insert(gameData)
      .select()
      .single();
    
    if (gameError) {
      console.error('Error creating game:', gameError);
      return { error: gameError };
    }
    
    console.log("Game created successfully:", game);
    
    // Ensure current user is in the players list
    const allPlayers = [...opts.players];

    if (!allPlayers.some(p => p.id === user.id)) {
      allPlayers.push({
        id: user.id,
        name: user.user_metadata?.name || user.email?.split('@')[0] || 'Me',
        email: user.email
      });
    }

    // Split into real players and manual players
    const realPlayers = allPlayers.filter(p => !p.isManual);
    const manualPlayers = allPlayers.filter(p => p.isManual && p.manualPlayerId);

    // Insert real players into game_players
    if (realPlayers.length > 0) {
      const gamePlayers = realPlayers.map(player => ({
        game_id: game.id,
        player_id: player.id
      }));

      console.log("Adding real players:", gamePlayers);

      const { error: playersError } = await supabase
        .from('game_players')
        .insert(gamePlayers);

      if (playersError) {
        console.error('Error adding players to game:', playersError);
        await supabase.from('games').delete().eq('id', game.id);
        return { error: playersError };
      }
    }

    // Insert manual players into game_manual_players
    if (manualPlayers.length > 0) {
      const gameManualPlayers = manualPlayers.map(player => ({
        game_id: game.id,
        manual_player_id: player.manualPlayerId!
      }));

      console.log("Adding manual players:", gameManualPlayers);

      const { error: manualError } = await supabase
        .from('game_manual_players')
        .insert(gameManualPlayers);

      if (manualError) {
        console.error('Error adding manual players to game:', manualError);
        await supabase.from('games').delete().eq('id', game.id);
        return { error: manualError };
      }
    }
    
    console.log("Game creation process completed successfully");
    return { data: game };
  } catch (error) {
    console.error('Unexpected error in createGame:', error);
    return { error };
  }
};

export const joinGame = async (gameId: string, userId: string) => {
  // Check if the user is already a player in this game
  const { data: existingPlayer } = await supabase
    .from('game_players')
    .select('id')
    .eq('game_id', gameId)
    .eq('player_id', userId)
    .maybeSingle();
    
  if (existingPlayer) {
    return { data: existingPlayer, message: 'Already a player in this game' };
  }
  
  // Add the user as a player
  const { data, error } = await supabase
    .from('game_players')
    .insert({ game_id: gameId, player_id: userId })
    .select();
    
  if (error) {
    console.error('Error joining game:', error);
    return { error };
  }
  
  return { data, message: 'Successfully joined game' };
};
